import { DesktopAppFrame } from "@/components/desktop/DesktopChrome";
import { EquipmentItem } from "@/components/equipment-item";
import { ThemedText } from "@/components/themed-text";
import { DocumentPicker } from "@/components/ui/document-picker";
import {
  UploadSourceModal,
  type UploadSource,
} from "@/components/ui/upload-source-modal";
import { VehicleRegistrationInput } from "@/components/ui/vehicle-registration-input";
import { BrandColors, Colors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { useVehicleCheck } from "@/hooks/useVehicleCheck";
import {
  assetToRNFile,
  imagePickerPresets,
  normalizeImageAsset,
  pickDocumentFile,
  pickImageFromCamera,
  pickImageFromLibrary,
  validateFileSize,
  webFileToAsset,
  type ImagePickerAsset
} from "@/lib/image-picker";
import vehicleService from "@/lib/services/vehicles";
import { showToast } from "@/lib/toast";
import {
  EQUIPMENT_OPTIONS,
  SERVICES_OPTIONS,
  VehicleData,
  vehicleSchema,
} from "@/lib/validation/vehicle";
import { zodResolver } from "@hookform/resolvers/zod";
import Fontisto from "@react-native-vector-icons/fontisto";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { HelperText } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

type VehicleDocumentField = "vehicle_photo" | "vehicle_insurance";

export default function CreateVehicleScreen() {
  const params = useLocalSearchParams<{ addVehicle?: string }>();
  const { refreshUser } = useAuth();
  const desktop = useDesktopLayout();
  const router = useRouter();
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];

  const form = useForm<VehicleData>({
    resolver: zodResolver(vehicleSchema) as any,
    defaultValues: {
      registration_number: "",
      make: "",
      model: "",
      vin: "",
      year: null,
      mileage: "",
      vehicle_type: "under_3.5t",
      services_enabled: [],
      equipment: [],
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = form;

  const registrationNumber = watch("registration_number");
  const [vehiclePhoto, setVehiclePhoto] = useState<ImagePickerAsset | null>(
    null,
  );
  const [vehicleInsurance, setVehicleInsurance] =
    useState<ImagePickerAsset | null>(null);
  const [uploadTarget, setUploadTarget] = useState<{
    fileName: VehicleDocumentField;
    label: string;
  } | null>(null);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);

  const {
    vehicleCheckData,
    checkingVehicle,
    vehicleCheckError,
    vehicleCategory,
  } = useVehicleCheck(registrationNumber);

  useEffect(() => {
    if (vehicleCheckData) {
      if (vehicleCheckData.make)
        setValue("make", vehicleCheckData.make.toLowerCase());
      if (vehicleCheckData.model)
        setValue("model", vehicleCheckData.model.toLowerCase());
      if (vehicleCheckData.year) setValue("year", vehicleCheckData.year);
      if (vehicleCheckData.vin) setValue("vin", vehicleCheckData.vin);

      const category = vehicleCheckData.vehicle_category;
      if (
        category === "under_3.5t" ||
        category === "3.5-7.5t" ||
        category === "over_7.5t"
      ) {
        setValue("vehicle_type", category);
      }
    }
  }, [vehicleCheckData]);

  const handlePick = (fileName: VehicleDocumentField, label: string) => {
    setUploadTarget({ fileName, label });
    setIsUploadModalVisible(true);
  };

  const handleUploadSourceSelect = async (source: UploadSource) => {
    if (!uploadTarget) {
      return;
    }

    const asset =
      source === "camera"
        ? await pickImageFromCamera(
            imagePickerPresets.document,
            true,
            "We need access to your camera to take photos.",
          )
        : source === "library"
          ? await pickImageFromLibrary(imagePickerPresets.document)
          : await pickDocumentFile();

    await handlePickedAsset(uploadTarget.fileName, asset);
  };

  const handleDropFile = (file: File) => {
    if (!uploadTarget) {
      return;
    }

    handlePickedAsset(uploadTarget.fileName, webFileToAsset(file));
  };

  const handlePickedAsset = async (
    fileName: VehicleDocumentField,
    asset: ImagePickerAsset | null,
  ) => {
    if (!asset) {
      return;
    }

    clearErrors(fileName);

    const normalizedAsset = await normalizeImageAsset(asset);
    const { isValid, message } = validateFileSize(normalizedAsset);
    if (!isValid) {
      setError(fileName, {
        message: message,
      } as any);
      return;
    }

    if (fileName === "vehicle_photo") {
      setVehiclePhoto(normalizedAsset);
    } else {
      setVehicleInsurance(normalizedAsset);
    }
    setValue(fileName, assetToRNFile(normalizedAsset, fileName));
  };

  const onSubmit = async (data: VehicleData) => {
    try {
      const vehicle = await vehicleService.createVehicle(data);

      showToast.success("Vehicle created successfully");
      if (params.addVehicle === "true") {
        router.replace("/complete-registration");
        return;
      }
      // Navigate back after a short delay
      setTimeout(() => {
        router.replace(`/vehicles/${vehicle.id}`);
      }, 1500);
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message || "Failed to register vehicle",
      );
    }
  };

  const pageColor =
    desktop && colorScheme !== "dark"
      ? BrandColors.backgroundGray
      : theme.background;
  const cardStyle = desktop && [
    styles.desktopCard,
    {
      backgroundColor: colorScheme === "dark" ? theme.card : theme.background,
      borderColor: theme.border,
    },
  ];

  const renderSectionTitle = (title: string, required = false) =>
    desktop ? (
      <View style={styles.desktopSectionTitleRow}>
        <ThemedText type="smallSemiBold">{title}</ThemedText>
        {required && <View style={styles.requiredDot} />}
      </View>
    ) : (
      <ThemedText style={styles.sectionTitle} type="subtitle">
        {title}{" "}
        {required && (
          <Fontisto name="asterisk" size={10} color={BrandColors.error} />
        )}
      </ThemedText>
    );

  return (
    <DesktopAppFrame>
      <SafeAreaView
        style={[styles.safeArea, { backgroundColor: pageColor }]}
        edges={[]}
      >
        <Stack.Screen
          options={{
            title: "Add Vehicle",
            headerShown: !desktop,
            headerStyle: {
              backgroundColor: theme.background,
            },
            headerTintColor: theme.text,
            headerBackTitle: "Back",
          }}
        />
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          style={styles.container}
        >
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={
              desktop ? styles.desktopScroll : styles.scrollContent
            }
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
          >
            {desktop && (
              <View style={styles.desktopHeader}>
                <TouchableOpacity
                  onPress={() => router.back()}
                  style={[
                    styles.desktopBackButton,
                    {
                      backgroundColor:
                        colorScheme === "dark" ? theme.card : theme.background,
                      borderColor: theme.border,
                    },
                  ]}
                  accessibilityRole="button"
                  accessibilityLabel="Back"
                >
                  <Ionicons name="chevron-back" size={20} color={theme.text} />
                </TouchableOpacity>
                <ThemedText type="subtitle" style={styles.desktopTitle}>
                  Add Vehicle
                </ThemedText>
              </View>
            )}
            <View style={desktop && styles.desktopContent}>
              <View style={styles.section}>
                {desktop ? (
                  <ThemedText
                    type="extraSmallSemiBold"
                    style={styles.desktopLabel}
                  >
                    Vehicle registration
                  </ThemedText>
                ) : (
                  renderSectionTitle("Vehicle Details", true)
                )}
                <Controller
                  control={control}
                  name="registration_number"
                  render={({ field: { value, onChange } }) => (
                    <View>
                      <VehicleRegistrationInput
                        registration={value}
                        onChangeRegistration={onChange}
                        isChecking={checkingVehicle}
                        error={vehicleCheckError}
                        vehicleData={vehicleCheckData}
                        vehicleCategory={vehicleCategory}
                      />
                      {errors.registration_number && (
                        <HelperText type="error" style={styles.errorText}>
                          {errors.registration_number?.message}
                        </HelperText>
                      )}
                    </View>
                  )}
                />
              </View>

              <View style={[styles.section, cardStyle]}>
                {renderSectionTitle("Required Documents", true)}

                <View style={desktop && styles.desktopRow}>
                  <View style={desktop && styles.desktopColumn}>
                    <DocumentPicker
                      label="Vehicle Photo"
                      asset={vehiclePhoto}
                      onPick={() => handlePick("vehicle_photo", "Vehicle Photo")}
                      required
                      allowPdf
                      desktop={desktop}
                    />
                    {errors.vehicle_photo && (
                      <HelperText type="error" style={styles.errorText}>
                        {errors.vehicle_photo.message}
                      </HelperText>
                    )}
                  </View>

                  <View style={desktop && styles.desktopColumn}>
                    <DocumentPicker
                      label="Vehicle Insurance"
                      asset={vehicleInsurance}
                      onPick={() =>
                        handlePick("vehicle_insurance", "Vehicle Insurance")
                      }
                      required
                      allowPdf
                      desktop={desktop}
                    />
                    {errors.vehicle_insurance && (
                      <HelperText type="error" style={styles.errorText}>
                        {errors.vehicle_insurance.message}
                      </HelperText>
                    )}
                  </View>
                </View>
              </View>

              <View style={[styles.section, cardStyle]}>
                {renderSectionTitle("Offered Services", true)}

                <View
                  style={
                    desktop
                      ? styles.desktopGrid
                      : [
                          styles.equipmentContainer,
                          {
                            borderColor: theme.border,
                            backgroundColor: theme.background,
                          },
                        ]
                  }
                >
                  <Controller
                    control={control}
                    name="services_enabled"
                    render={({ field: { value, onChange } }) => (
                      <>
                        {SERVICES_OPTIONS.map((service, index) => (
                          <View
                            key={service.value}
                            style={
                              desktop && [
                                styles.desktopGridItem,
                                { borderTopColor: theme.border },
                              ]
                            }
                          >
                            <EquipmentItem
                              label={service.label}
                              value={(value || []).includes(service.value)}
                              onValueChange={(checked) => {
                                const current = value || [];
                                const newValue = checked
                                  ? [...current, service.value]
                                  : current.filter(
                                      (v: string) => v !== service.value,
                                    );
                                onChange(newValue);
                              }}
                              isLast={
                                desktop || index === SERVICES_OPTIONS.length - 1
                              }
                            />
                          </View>
                        ))}
                      </>
                    )}
                  />
                </View>
                {errors.services_enabled && (
                  <HelperText type="error" style={styles.errorText}>
                    {errors.services_enabled.message}
                  </HelperText>
                )}
              </View>

              <View style={[styles.section, cardStyle]}>
                {renderSectionTitle("Vehicle Equipment")}

                <View
                  style={
                    desktop
                      ? styles.desktopGrid
                      : [
                          styles.equipmentContainer,
                          {
                            borderColor: theme.border,
                            backgroundColor: theme.background,
                          },
                        ]
                  }
                >
                  <Controller
                    control={control}
                    name="equipment"
                    render={({ field: { value, onChange } }) => (
                      <>
                        {EQUIPMENT_OPTIONS.map((equipment, index) => (
                          <View
                            key={equipment.value}
                            style={
                              desktop && [
                                styles.desktopGridItem,
                                { borderTopColor: theme.border },
                              ]
                            }
                          >
                            <EquipmentItem
                              label={equipment.label}
                              value={(value || []).includes(equipment.value)}
                              onValueChange={(checked) => {
                                const current = value || [];
                                const newValue = checked
                                  ? [...current, equipment.value]
                                  : current.filter(
                                      (v: string) => v !== equipment.value,
                                    );
                                onChange(newValue);
                              }}
                              isLast={
                                desktop ||
                                index === EQUIPMENT_OPTIONS.length - 1
                              }
                            />
                          </View>
                        ))}
                      </>
                    )}
                  />
                </View>
              </View>

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, isSubmitting && styles.buttonDisabled]}
                  onPress={handleSubmit(onSubmit)}
                  disabled={isSubmitting}
                >
                  <Ionicons name="checkmark-circle" size={20} color="#fff" />
                  <ThemedText style={styles.buttonText} type="defaultSemiBold">
                    {isSubmitting ? "Creating..." : "Create Vehicle"}
                  </ThemedText>
                </TouchableOpacity>
              </View>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>
        <UploadSourceModal
          visible={isUploadModalVisible}
          title={`Upload ${uploadTarget?.label ?? ""}`}
          onClose={() => setIsUploadModalVisible(false)}
          onSelect={handleUploadSourceSelect}
          onDropFile={handleDropFile}
        />
      </SafeAreaView>
    </DesktopAppFrame>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 40,
  },
  desktopScroll: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 48,
  },
  desktopContent: {
    width: "100%",
    maxWidth: 560,
    alignSelf: "center",
  },
  desktopHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 40,
    marginBottom: 24,
  },
  desktopBackButton: {
    position: "absolute",
    left: 0,
    width: 36,
    height: 36,
    borderRadius: 18,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  desktopTitle: {
    fontSize: 22,
    fontWeight: "700",
  },
  desktopLabel: {
    marginBottom: 8,
  },
  desktopCard: {
    borderWidth: 1,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
  },
  desktopSectionTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 12,
  },
  requiredDot: {
    width: 5,
    height: 5,
    borderRadius: 3,
    backgroundColor: BrandColors.error,
  },
  desktopRow: {
    flexDirection: "row",
    gap: 12,
  },
  desktopColumn: {
    flex: 1,
  },
  desktopGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    columnGap: 16,
  },
  desktopGridItem: {
    width: "48%",
    flexGrow: 1,
    borderTopWidth: 1,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 16,
  },
  typeButtons: {
    flexDirection: "row",
    gap: 8,
    flexWrap: "wrap",
  },
  typeButton: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#ddd",
    backgroundColor: "#fff",
  },
  typeButtonActive: {
    backgroundColor: "#007AFF",
    borderColor: "#007AFF",
  },
  typeButtonText: {
    fontSize: 14,
    color: "#666",
    fontWeight: "500",
  },
  typeButtonTextActive: {
    color: "#fff",
    fontWeight: "600",
  },
  inputContainer: {
    marginBottom: 16,
    marginTop: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: "500",
    color: "#1a1a1a",
    marginBottom: 8,
  },
  required: {
    fontSize: 12,
    color: "#666",
    fontWeight: "400",
  },
  buttonContainer: {
    marginTop: 8,
    gap: 12,
  },
  button: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BrandColors.primary,
    padding: 16,
    borderRadius: 12,
    gap: 8,
  },
  buttonDisabled: {
    backgroundColor: "#ccc",
  },
  buttonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
  equipmentContainer: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 8,
  },
  manualInput: {
    height: 48,
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 12,
    fontSize: 16,
  },
  errorText: {
    fontSize: 12,
  },
});
